import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { HeartPulse, Camera, User, Loader2, ArrowLeft, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditProfilePage() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    photo_base64: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_email: "",
    emergency_contact_relationship: "",
    allergies: "",
    has_dementia: false,
    additional_notes: ""
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchProfile();
  }, [profileId, navigate]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/profiles/${profileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const profile = response.data;
      setFormData({
        name: profile.name || "",
        photo_base64: profile.photo_base64 || "",
        emergency_contact_name: profile.emergency_contact_name || "",
        emergency_contact_phone: profile.emergency_contact_phone || "",
        emergency_contact_email: profile.emergency_contact_email || "",
        emergency_contact_relationship: profile.emergency_contact_relationship || "",
        allergies: profile.allergies || "",
        has_dementia: profile.has_dementia || false,
        additional_notes: profile.additional_notes || ""
      });
      if (profile.photo_base64) {
        setPhotoPreview(`data:image/jpeg;base64,${profile.photo_base64}`);
      }
    } catch (error) {
      toast.error("Failed to load profile");
      navigate("/dashboard/profiles");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Photo must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        setFormData({ ...formData, photo_base64: base64 });
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.emergency_contact_name || !formData.emergency_contact_phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/profiles/${profileId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Profile updated successfully!");
      navigate("/dashboard/profiles");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container-app flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/profiles">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Edit Profile
            </span>
          </div>
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
              alt="Stress-Free Aging" 
              className="h-10 w-auto"
            />
          </Link>
        </div>
      </header>

      <main className="container-app py-8">
        <div className="max-w-lg mx-auto">
          <Card className="border-slate-200 shadow-lg">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Profile Information</CardTitle>
              <CardDescription>
                Update emergency contact information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Photo Upload */}
                <div className="text-center">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoChange}
                    accept="image/*"
                    className="hidden"
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="cursor-pointer inline-block"
                  >
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Profile preview"
                        className="w-32 h-32 rounded-full object-cover border-4 border-teal-600 mx-auto"
                      />
                    ) : (
                      <div className="w-32 h-32 bg-slate-100 rounded-full flex flex-col items-center justify-center border-2 border-dashed border-slate-300 mx-auto hover:border-teal-500 transition-colors">
                        <Camera className="h-8 w-8 text-slate-400 mb-1" />
                        <span className="text-xs text-slate-500">Add Photo</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-2">Tap to change photo</p>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="Enter full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-12"
                    data-testid="edit-name-input"
                  />
                </div>

                {/* Emergency Contact Section */}
                <div className="pt-4 border-t border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Emergency Contact
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_name">Contact Name *</Label>
                      <Input
                        id="emergency_contact_name"
                        placeholder="e.g., John Smith"
                        value={formData.emergency_contact_name}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                        required
                        className="h-12"
                        data-testid="edit-contact-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_phone">Phone Number *</Label>
                      <Input
                        id="emergency_contact_phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.emergency_contact_phone}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                        required
                        className="h-12"
                        data-testid="edit-contact-phone-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_relationship">Relationship</Label>
                      <Input
                        id="emergency_contact_relationship"
                        placeholder="e.g., Son, Daughter, Caregiver"
                        value={formData.emergency_contact_relationship}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_relationship: e.target.value })}
                        className="h-12"
                        data-testid="edit-contact-relationship-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_email">Email (for scan notifications)</Label>
                      <Input
                        id="emergency_contact_email"
                        type="email"
                        placeholder="guardian@email.com"
                        value={formData.emergency_contact_email}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_email: e.target.value })}
                        className="h-12"
                        data-testid="edit-contact-email-input"
                      />
                      <p className="text-xs text-slate-500">Receive email alerts when the bracelet is scanned</p>
                    </div>
                  </div>
                </div>

                {/* Medical Info Section */}
                <div className="pt-4 border-t border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Medical Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-pink-50 rounded-lg border border-pink-200">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="h-5 w-5 text-pink-600" />
                        <div>
                          <Label htmlFor="has_dementia" className="text-pink-800 font-medium">
                            Has Dementia / Memory Issues
                          </Label>
                          <p className="text-sm text-pink-600">Alert responders to be patient</p>
                        </div>
                      </div>
                      <Switch
                        id="has_dementia"
                        checked={formData.has_dementia}
                        onCheckedChange={(checked) => setFormData({ ...formData, has_dementia: checked })}
                        data-testid="edit-dementia-switch"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="allergies">Allergies (keep vague for privacy)</Label>
                      <Textarea
                        id="allergies"
                        placeholder="e.g., Penicillin allergy"
                        value={formData.allergies}
                        onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                        rows={2}
                        data-testid="edit-allergies-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="additional_notes">Additional Notes</Label>
                      <Textarea
                        id="additional_notes"
                        placeholder="Any other important information..."
                        value={formData.additional_notes}
                        onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                        rows={2}
                        data-testid="edit-notes-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Link to="/dashboard/profiles" className="flex-1">
                    <Button type="button" variant="outline" className="w-full h-12">
                      Cancel
                    </Button>
                  </Link>
                  <Button
                    type="submit"
                    className="flex-1 h-12 bg-teal-600 hover:bg-teal-700 text-white font-semibold"
                    disabled={saving}
                    data-testid="save-profile-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-5 w-5" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
